import { COMPONENT_REGISTRY, PERMISSION_LEVELS, ROLE_HIERARCHY } from '../config/componentRegistry';

/**
 * Permission Engine - Core logic for determining component visibility and edit permissions
 * This class handles the complex logic of evaluating permissions based on:
 * - Default role-based permissions from component registry
 * - User-specific permission overrides
 * - Environment restrictions
 * - Authentication status
 */
export class PermissionEngine {
  constructor() {
    this.cache = new Map();
  }

  /**
   * Compute permissions for a user based on all factors
   * @param {Object} params - Permission computation parameters
   * @param {Array} params.userRoles - User's roles array
   * @param {Object} params.rolesMapped - User's roles mapped object (e.g., {Admin: true, Engineer: false})
   * @param {string} params.userObjectId - User's unique identifier
   * @param {Object} params.userPermissions - User-specific permission overrides from API
   * @param {Object} params.runtimePolicies - Runtime component policies keyed by componentKey/environment
   * @param {string} params.environment - Current environment (Development, UAT, Production)
   * @param {boolean} params.isAuthenticated - Whether user is authenticated
   * @returns {Object} Computed permissions object
   */
  computePermissions(params) {
    const {
      userRoles = [],
      rolesMapped = {},
      userObjectId,
      userPermissions = {},
      runtimePolicies = {},
      environment = 'Production',
      isAuthenticated = false
    } = params;

    const cacheKey = this.createCacheKey({
      userRoles,
      rolesMapped,
      userObjectId,
      userPermissions,
      runtimePolicies,
      environment,
      isAuthenticated
    });
    
    // Return cached result if available
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    const computedPermissions = {};

    // If not authenticated, return empty permissions
    if (!isAuthenticated) {
      this.cache.set(cacheKey, computedPermissions);
      return computedPermissions;
    }

    // Process each component in the registry
    Object.entries(COMPONENT_REGISTRY).forEach(([componentKey, config]) => {
      const runtimePolicy = this.resolveRuntimePolicy(componentKey, environment, runtimePolicies);
      computedPermissions[componentKey] = this.computeComponentPermission(
        componentKey,
        config,
        userRoles,
        rolesMapped,
        userPermissions,
        environment,
        runtimePolicy
      );
    });

    // Cache the result
    this.cache.set(cacheKey, computedPermissions);
    return computedPermissions;
  }

  /**
   * Compute permission for a single component
   * @private
   */
  computeComponentPermission(componentKey, config, userRoles, rolesMapped, userPermissions, environment, runtimePolicy) {
    // Global policy switch for a component in this environment.
    if (runtimePolicy && runtimePolicy.enabled === false) {
      return PERMISSION_LEVELS.HIDDEN;
    }

    // Check if user has specific override for this component
    if (userPermissions[componentKey]) {
      const overridePermission = userPermissions[componentKey];
      const isEnvironmentAllowed = runtimePolicy
        ? this.isPolicyEnvironmentAllowed(runtimePolicy, environment)
        : this.isEnvironmentAllowed(config, environment);

      // Validate override against environment restrictions.
      if (isEnvironmentAllowed) {
        return overridePermission;
      } else {
        return PERMISSION_LEVELS.HIDDEN; // Hide if environment not allowed
      }
    }

    // Runtime policies are applied first, but unresolved policy payloads
    // should still fall back to static registry role defaults.
    if (runtimePolicy) {
      const policyPermission = this.getPolicyBasedPermission(runtimePolicy, userRoles, rolesMapped);
      if (policyPermission) {
        return policyPermission;
      }
    }

    // Check environment restrictions first
    if (!this.isEnvironmentAllowed(config, environment)) {
      return PERMISSION_LEVELS.HIDDEN;
    }

    // Determine permission based on role-based defaults
    return this.getRoleBasedPermission(config, userRoles, rolesMapped);
  }

  /**
   * Check if component is allowed in current environment
   * @private
   */
  isEnvironmentAllowed(config, currentEnvironment) {
    const allowedEnvironments = config.environments || ['all'];
    const normalizedCurrent = this.normalizeEnvironment(currentEnvironment);
    
    if (allowedEnvironments.some((environment) => String(environment).toLowerCase() === 'all')) {
      return true;
    }
    
    return allowedEnvironments
      .map((environment) => this.normalizeEnvironment(environment))
      .includes(normalizedCurrent);
  }

  /**
   * Check if a runtime policy should apply in the current environment
   * @private
   */
  isPolicyEnvironmentAllowed(policy, currentEnvironment) {
    const policyEnvironments = this.normalizePolicyEnvironments(
      policy.environments ??
      policy.Environments ??
      policy.environment ??
      policy.Environment
    );
    const normalizedCurrent = this.normalizeEnvironment(currentEnvironment);

    if (policyEnvironments.length === 0 || policyEnvironments.includes('all')) {
      return true;
    }

    return policyEnvironments.includes(normalizedCurrent);
  }

  /**
   * Normalize policy environment input to a list
   * @private
   */
  normalizePolicyEnvironments(input) {
    if (Array.isArray(input)) {
      return input
        .map((value) => this.normalizeEnvironment(value))
        .filter(Boolean);
    }

    if (typeof input === 'string' && input.trim()) {
      const trimmed = input.trim();

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((value) => this.normalizeEnvironment(value))
            .filter(Boolean);
        }
      } catch (_) {
        return trimmed
          .split(',')
          .map((value) => this.normalizeEnvironment(value))
          .filter(Boolean);
      }

      const normalized = this.normalizeEnvironment(trimmed);
      return normalized ? [normalized] : [];
    }

    const normalized = this.normalizeEnvironment(input);
    return normalized ? [normalized] : [];
  }

  /**
   * Resolve runtime policy for a given component/environment pair
   * @private
   */
  resolveRuntimePolicy(componentKey, currentEnvironment, runtimePolicies = {}) {
    const componentPolicies = runtimePolicies?.[componentKey];
    if (!componentPolicies) {
      return null;
    }

    const normalizedCurrent = this.normalizeEnvironment(currentEnvironment);

    return (
      componentPolicies[normalizedCurrent] ||
      componentPolicies[currentEnvironment] ||
      componentPolicies.all ||
      null
    );
  }

  /**
   * Evaluate permission from runtime policy role mappings.
   *
   * Multi-role rule: Each of the user's roles is evaluated independently.
   * A role gets its explicit policy permission if one is defined, or the
   * policy's defaultPermission if not. The least-restrictive (highest-rank)
   * result across all of the user's roles wins — so a user with both an
   * "Accounting" (hidden) role and an "Apps" (no explicit rule) role will
   * receive the defaultPermission granted to Apps rather than being blocked
   * by the Accounting denial.
   *
   * @private
   */
  getPolicyBasedPermission(policy, userRoles, rolesMapped) {
    const rolePermissions = this.normalizePolicyRolePermissions(
      policy.rolePermissions ?? policy.RolePermissions
    );

    const defaultPermission = this.normalizePermission(
      policy.defaultPermission ?? policy.DefaultPermission,
      null
    );
    const hasExplicitDefaultPermission = defaultPermission !== null;

    // Build a fast lookup of explicit per-role rules defined in this policy.
    const policyRuleByRole = new Map();
    rolePermissions.forEach((entry) => {
      if (entry.roleName) {
        policyRuleByRole.set(entry.roleName, entry.permission);
      }
    });

    // Collect the full set of roles the current user actually holds.
    const userRoleSet = new Set([
      ...(Array.isArray(userRoles) ? userRoles : []),
      ...Object.keys(rolesMapped || {}).filter((role) => rolesMapped[role] === true),
    ]);

    // For each of the user's roles, determine its effective permission under
    // this policy, then keep the least-restrictive result (highest rank).
    let bestRank = -1;
    let bestMatch = null;

    userRoleSet.forEach((role) => {
      let effectivePermission;

      if (policyRuleByRole.has(role)) {
        // The policy has an explicit rule for this role — use it.
        effectivePermission = policyRuleByRole.get(role);
      } else if (hasExplicitDefaultPermission) {
        // No explicit rule for this role; fall back to the policy default.
        effectivePermission = defaultPermission;
      } else {
        // No rule and no default — this role doesn't resolve via policy;
        // the caller will fall back to the static registry.
        return;
      }

      const rank = this.permissionRank(effectivePermission);
      if (rank > bestRank) {
        bestRank = rank;
        bestMatch = effectivePermission;
      }
    });

    if (bestMatch !== null) {
      return this.normalizePermission(bestMatch);
    }

    // If the policy requires authentication, grant view access to any
    // authenticated user whose roles didn't resolve a specific permission.
    if (policy.requiresAuth || policy.RequiresAuth) {
      return PERMISSION_LEVELS.VIEW;
    }

    // No applicable runtime rule for this user; caller should use registry fallback.
    return null;
  }

  /**
   * Normalize role permission payload from API
   * @private
   */
  normalizePolicyRolePermissions(rolePermissions) {
    if (Array.isArray(rolePermissions)) {
      return rolePermissions
        .map((entry) => {
          const roleName = entry?.roleName || entry?.RoleName || '';
          const permission = this.normalizePermission(
            entry?.permission ?? entry?.Permission,
            null
          );

          return {
            roleName,
            permission,
          };
        })
        .filter((entry) => entry.roleName && entry.permission !== null);
    }

    if (typeof rolePermissions === 'string' && rolePermissions.trim()) {
      try {
        return this.normalizePolicyRolePermissions(JSON.parse(rolePermissions));
      } catch (_) {
        return [];
      }
    }

    return [];
  }

  /**
   * Normalize environment values from different conventions
   * @private
   */
  normalizeEnvironment(environment) {
    const normalized = String(environment || '').toLowerCase();
    if (normalized === 'development') return 'dev';
    if (normalized === 'production') return 'production';
    if (normalized === 'uat') return 'uat';
    if (normalized === 'dev') return 'dev';
    if (normalized === 'prod') return 'production';
    if (normalized === 'all') return 'all';
    return normalized;
  }

  /**
   * Normalize permission values from API payloads
   * @private
   */
  normalizePermission(permission, fallback = PERMISSION_LEVELS.HIDDEN) {
    const normalized = String(permission || '').toLowerCase();
    if (normalized === PERMISSION_LEVELS.EDIT) return PERMISSION_LEVELS.EDIT;
    if (normalized === PERMISSION_LEVELS.VIEW) return PERMISSION_LEVELS.VIEW;
    if (normalized === PERMISSION_LEVELS.HIDDEN) return PERMISSION_LEVELS.HIDDEN;
    return fallback;
  }

  /**
   * Permission rank helper used to resolve multi-role policy matches
   * @private
   */
  permissionRank(permission) {
    const normalized = this.normalizePermission(permission);
    if (normalized === PERMISSION_LEVELS.EDIT) return 2;
    if (normalized === PERMISSION_LEVELS.VIEW) return 1;
    return 0;
  }

  /**
   * Get role-based permission for a component
   * @private
   */
  getRoleBasedPermission(config, userRoles, rolesMapped) {
    // Check for edit permission first
    if (config.editRoles) {
      if (this.hasAnyRole(config.editRoles, userRoles, rolesMapped)) {
        return PERMISSION_LEVELS.EDIT;
      }
    }

    // Check for view permission
    if (config.viewRoles) {
      if (this.hasAnyRole(config.viewRoles, userRoles, rolesMapped)) {
        return PERMISSION_LEVELS.VIEW;
      }
    }

    // Check for default roles (implies view permission)
    if (config.defaultRoles) {
      if (this.hasAnyRole(config.defaultRoles, userRoles, rolesMapped)) {
        return PERMISSION_LEVELS.VIEW;
      }
    }

    // Allow components that are visible to any signed-in user.
    if (config.requiresAuth) {
      return PERMISSION_LEVELS.VIEW;
    }

    // No permission found
    return PERMISSION_LEVELS.HIDDEN;
  }

  /**
   * Check if user has any of the required roles
   * @private
   */
  hasAnyRole(requiredRoles, userRoles, rolesMapped) {
    return requiredRoles.some(role => {
      // Check in mapped roles object (preferred)
      if (rolesMapped[role] === true) {
        return true;
      }
      
      // Fallback to array check
      return userRoles.includes(role);
    });
  }

  /**
   * Create cache key for permission computation
   * @private
   */
  createCacheKey(params) {
    const {
      userRoles = [],
      rolesMapped = {},
      userObjectId,
      userPermissions = {},
      runtimePolicies = {},
      environment = 'Production',
      isAuthenticated = false
    } = params;

    return JSON.stringify({
      userRoles: [...userRoles].sort(), // Create a copy before sorting
      rolesMapped,
      userObjectId,
      userPermissions,
      runtimePolicies,
      environment,
      isAuthenticated
    });
  }

  /**
   * Clear the permission cache
   * Call this when user roles or permissions change
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Get permission level for a specific component
   * @param {string} componentKey - Component identifier
   * @param {Object} computedPermissions - Pre-computed permissions object
   * @returns {string} Permission level (hidden, view, edit)
   */
  getComponentPermission(componentKey, computedPermissions) {
    return computedPermissions[componentKey] || PERMISSION_LEVELS.HIDDEN;
  }

  /**
   * Check if user can view a component
   * @param {string} componentKey - Component identifier
   * @param {Object} computedPermissions - Pre-computed permissions object
   * @returns {boolean} True if user can view the component
   */
  canView(componentKey, computedPermissions) {
    const permission = this.getComponentPermission(componentKey, computedPermissions);
    return permission === PERMISSION_LEVELS.VIEW || permission === PERMISSION_LEVELS.EDIT;
  }

  /**
   * Check if user can edit a component
   * @param {string} componentKey - Component identifier  
   * @param {Object} computedPermissions - Pre-computed permissions object
   * @returns {boolean} True if user can edit the component
   */
  canEdit(componentKey, computedPermissions) {
    const permission = this.getComponentPermission(componentKey, computedPermissions);
    return permission === PERMISSION_LEVELS.EDIT;
  }

  /**
   * Get user's highest role level (for admin interfaces)
   * @param {Array} userRoles - User's roles
   * @returns {number} Highest role level
   */
  getUserRoleLevel(userRoles) {
    let highestLevel = 0;
    userRoles.forEach(role => {
      const level = ROLE_HIERARCHY[role] || 0;
      if (level > highestLevel) {
        highestLevel = level;
      }
    });
    return highestLevel;
  }

  /**
   * Check if user is admin (for admin-only features)
   * @param {Array} userRoles - User's roles
   * @param {Object} rolesMapped - User's roles mapped object
   * @returns {boolean} True if user is admin
   */
  isAdmin(userRoles, rolesMapped) {
    return rolesMapped?.Admin === true || userRoles.includes('Admin');
  }
}

// Export singleton instance
export const permissionEngine = new PermissionEngine();
